import {
  Box,
  Flex,
  Icon,
  Text,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Link,
  useDisclosure,
  Button,
  IconButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  useTheme,
  DrawerOverlay,
  useBreakpointValue,
} from '@chakra-ui/react';
import { MdFilterList, MdList, MdMap } from 'react-icons/md';
import { BsGrid3X3GapFill as GridIcon } from 'react-icons/bs';
import { IoChevronForwardSharp, IoChevronDownSharp } from 'react-icons/io5';
import { ListingFilterItem, PriceRangeFilter } from 'src/components/molecules';
import { useAttributeSelector } from 'src/contexts/AttributeSelector';
import SortOptionSelect from 'src/components/molecules/sort-option-select';
import { capitalize, isEmpty } from 'lodash';
import {
  Attribute,
  AttributePreview,
  Category,
  CategoryPreview,
  RootCategory,
  Subcategory,
  ViewTypes,
} from 'src/api/v1-api';
import { categoryTypeHasPrice } from 'src/utils/categoryUtils';
import { useEffect, useState } from 'react';
import { hiddenFilterAttributeFormats } from 'src/constants/attributes';
import { BLACK } from 'src/constants/ui';
import { SortOption } from 'src/constants/sort-options';
import CategoryFilter from 'src/components/molecules/category-filter';
import useRequest from 'src/utils/hooks/useRequest';
import { useMarketplaceContext } from 'src/contexts/marketplace';

interface Props {
  category?: Category | RootCategory | CategoryPreview;
  handleUserChangedPriceRange?: React.Dispatch<React.SetStateAction<boolean>>;
  attributesPreview?: AttributePreview[];
  viewTypePreview?: string;
  hideFilters?: boolean;
}

const LISTING_VIEW_TYPES_POSITIONS = {
  [ViewTypes.GRID]: 1,
  [ViewTypes.LIST]: 2,
  [ViewTypes.MAP]: 3,
};

const ListingFilterItems = ({
  filteredAttributes,
}: {
  filteredAttributes: AttributePreview[] | Attribute[];
}) => {
  return (
    <>
      {filteredAttributes.map((attribute) => (
        <AccordionItem key={attribute.name}>
          {({ isExpanded }) => (
            <>
              <AccordionButton>
                <Box fontWeight="semibold" mb="2" flex="1" textAlign="left">
                  {attribute.name}
                </Box>
                {isExpanded ? <IoChevronDownSharp /> : <IoChevronForwardSharp />}
              </AccordionButton>
              <AccordionPanel>
                <ListingFilterItem key={attribute.name} attributeId={attribute.name} isMobileView />
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      ))}
    </>
  );
};

const ListingAttributes = ({
  category,
  handleUserChangedPriceRange,
  attributesPreview,
  viewTypePreview,
  hideFilters = false,
}: Props): JSX.Element => {
  const {
    attributes,
    useToggle,
    viewType,
    setViewType,
    setSelectedAttributes,
    setStagedAttributes,
    setStagedPriceRange,
    commitStagedAttributes,
    setPriceRange,
    sortOptions,
    selectedSort,
    setSelectedSort,
    selectedAttributes,
    priceRange,
    maxPrice,
    categoryInFilters,
    setCategoryInFilters,
  } = useAttributeSelector();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [filtersAppliedCount, setFiltersAppliedCount] = useState(0);
  const [totalAttributes, setTotalAttributes] = useState<Attribute[]>([]);
  const isMobileView = useBreakpointValue({ base: true, md: false });
  const theme = useTheme();
  const { marketplace } = useMarketplaceContext();
  const { api } = useRequest();

  const hasPrice = categoryTypeHasPrice(categoryInFilters?.type);
  const showViewType = attributesPreview ? viewTypePreview : viewType;
  const hasSubcategoriesForFilters = !!(categoryInFilters?.children ?? []).filter(
    (subcategory) => subcategory.position >= 0 && subcategory.image,
  ).length;

  const handleClear = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault();
    setSelectedAttributes({});
    setStagedAttributes({});
    setPriceRange([0, 0]);
    setCategoryInFilters(category);
    onClose();
  };

  const handleApply = () => {
    commitStagedAttributes();
    onClose();
  };

  useEffect(() => {
    setCategoryInFilters(category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const handleOnOpen = () => {
    // Initialize staged state from current selected state
    setStagedAttributes(selectedAttributes);
    setStagedPriceRange(priceRange);
    onOpen();
  };

  const handleOnClose = () => {
    // Reset staged state to match current selected state (cancel changes)
    setStagedAttributes(selectedAttributes);
    setStagedPriceRange(priceRange);
    setCategoryInFilters(category);
    onClose();
  };

  useEffect(() => {
    const params = [];
    if (priceRange[0] !== 0 || (priceRange[1] !== 0 && priceRange[1] != maxPrice)) {
      params.push({ value: `$${priceRange[0]} - $${priceRange[1]}`, name: 'price' });
    }
    for (const [key, values] of Object.entries(selectedAttributes)) {
      if (Array.isArray(values)) {
        params.push({ value: values.join(', '), name: key });
      }
    }
    let tempFiltersAppliedCount = 0;
    for (const [key, values] of Object.entries(params)) {
      if (values.name !== 'page' && values.name !== 'sort' && values.value !== '') {
        tempFiltersAppliedCount += 1;
      }
    }

    setFiltersAppliedCount(tempFiltersAppliedCount);
  }, [selectedAttributes, priceRange]);

  useEffect(() => {
    const attrs = (categoryInFilters?.attributes as Attribute[]) ?? attributes;
    const totalAttributes = attrs.filter((attribute) => {
      if ('filter_display' in attribute && !attribute.filter_display) {
        return false;
      }

      if (attribute?.nested_content_type_name === 'Submitted Lead') {
        return false;
      }

      if (hiddenFilterAttributeFormats.indexOf(attribute?.format.toLowerCase()) > -1) {
        return false;
      }

      return true;
    });
    setTotalAttributes(totalAttributes);
  }, [categoryInFilters?.attributes, attributes]);

  const sortOptionsShow = attributesPreview ? (['newest', 'oldest'] as SortOption[]) : sortOptions;

  const getListViewTypeIcon = (viewType: ViewTypes, props: any = {}) => {
    switch (viewType) {
      case ViewTypes.GRID:
        return <GridIcon {...props} />;
      case ViewTypes.LIST:
        return <MdList {...props} />;
      case ViewTypes.MAP:
        return <MdMap {...props} />;
    }
  };

  const enabledViewTypes = categoryInFilters?.functional.enabled_view_types ?? [
    ViewTypes.GRID,
    ViewTypes.MAP,
  ];

  const handleChangeCategory = async (newCategory: Subcategory) => {
    try {
      if (newCategory.id === categoryInFilters?.id || !marketplace?.id) return;

      const response = await api.marketplace.categories.getDetails(marketplace.id, newCategory.id);
      setCategoryInFilters(response);
    } catch (error) {
      console.error('Error changing category:', error);
    }
  };

  return (
    <Flex direction="column" gap="24px">
      <Flex direction="row" alignItems="flex-start" gap={{ base: '6px', lg: '10px' }}>
        {(useToggle || attributesPreview) && (
          <Flex direction="row" alignItems="center" gap={{ base: '6px', lg: '10px' }}>
            <Text fontWeight={600} hideBelow="md">
              View
            </Text>
            {enabledViewTypes
              .sort((a, b) => LISTING_VIEW_TYPES_POSITIONS[a] - LISTING_VIEW_TYPES_POSITIONS[b])
              .map((vT) => (
                <div key={vT}>
                  <IconButton
                    aria-label={`view-as-${vT}`}
                    fontSize={24}
                    variant={showViewType === vT ? 'solid' : 'outline'}
                    colorScheme={showViewType === vT ? 'primary' : 'gray'}
                    icon={getListViewTypeIcon(vT)}
                    onClick={() => setViewType(vT)}
                    height="42px"
                    width="42px"
                    {...(showViewType !== vT && {
                      color: theme.colors.mainText,
                      _hover: { color: BLACK, bgColor: theme.colors.gray[100] },
                    })}
                    {...(enabledViewTypes.length < 3 && {
                      hideFrom: 'md',
                    })}
                  />
                  <Button
                    aria-label={`view-as-${vT}`}
                    variant={showViewType === vT ? 'solid' : 'outline'}
                    colorScheme={showViewType === vT ? 'primary' : 'gray'}
                    rightIcon={getListViewTypeIcon(vT, { fontSize: 24 })}
                    onClick={() => setViewType(vT)}
                    height="42px"
                    {...(showViewType !== vT && {
                      color: theme.colors.mainText,
                      _hover: { color: BLACK, bgColor: theme.colors.gray[100] },
                    })}
                    {...(enabledViewTypes.length < 3
                      ? {
                          hideBelow: 'md',
                        }
                      : {
                          display: 'none',
                        })}
                  >
                    {capitalize(vT)}
                  </Button>
                </div>
              ))}
          </Flex>
        )}

        {/* Filter */}
        <Flex direction="row" alignItems="center" grow={1}>
          {!hideFilters && (
            <Button
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              colorScheme={filtersAppliedCount ? 'primary' : 'gray'}
              bgColor={filtersAppliedCount ? theme.colors.primary[500] : 'white'}
              maxWidth="200px"
              width="full"
              fontSize="md"
              type="button"
              px="3"
              height="42px"
              onClick={handleOnOpen}
              borderWidth="1px"
              textAlign="left"
              {...(theme.name === 'Magazine' && {
                color: filtersAppliedCount ? 'white' : theme.colors.primary[500],
                borderColor: theme.colors.primary[500],
              })}
            >
              <Text>Filters {filtersAppliedCount ? `(${filtersAppliedCount})` : ''}</Text>
              <Icon as={MdFilterList} fontSize="xl" />
            </Button>
          )}
        </Flex>
        {/* Sort */}
        {!isEmpty(sortOptionsShow) && (
          <Flex
            justifyContent="flex-end"
            flexShrink={{ base: 1, lg: 0 }}
            flexDirection="row"
            alignItems="center"
            columnGap="10px"
          >
            <Text fontWeight={600} hideBelow="md" display={'inline-block'} whiteSpace={'nowrap'}>
              Sort by
            </Text>
            <Flex grow={1}>
              <SortOptionSelect
                options={sortOptionsShow ?? []}
                value={selectedSort ?? ''}
                onChange={setSelectedSort}
              />
            </Flex>
          </Flex>
        )}
        <Drawer
          placement={isMobileView ? 'bottom' : 'right'}
          isOpen={isOpen}
          onClose={onClose}
          trapFocus={false}
          size="sm"
        >
          <DrawerOverlay />
          <DrawerContent roundedTop={theme.border_radius.border_radius_2} p="20px" pt="4 px">
            <DrawerHeader px="4" p="0">
              <Box textAlign="center" position="relative">
                <Text fontWeight="700" fontSize="24px">
                  Filters
                </Text>
                <Box position="absolute" right="0" top="0">
                  <Link
                    textDecor="underline"
                    fontWeight={400}
                    fontSize="16px"
                    onClick={handleClear}
                  >
                    Reset All
                  </Link>
                </Box>
              </Box>
            </DrawerHeader>
            <DrawerBody p="0">
              <Flex direction="column" pt="16px" gap="16px">
                <Accordion
                  allowToggle
                  maxH={{ base: 'calc(100vh - 300px)', md: 'calc(100vh - 210px)' }}
                  overflowY="auto"
                >
                  {hasSubcategoriesForFilters && (
                    <AccordionItem key="category-filter">
                      {({ isExpanded }) => (
                        <>
                          <AccordionButton>
                            <Box fontWeight="600" mb="2" flex="1" textAlign="left">
                              Category
                            </Box>
                            {isExpanded ? <IoChevronDownSharp /> : <IoChevronForwardSharp />}
                          </AccordionButton>
                          <AccordionPanel>
                            <CategoryFilter
                              category={categoryInFilters}
                              onCategoryChange={handleChangeCategory}
                              onCancel={() => setCategoryInFilters(category)}
                              isMobileView
                            />
                          </AccordionPanel>
                        </>
                      )}
                    </AccordionItem>
                  )}
                  {handleUserChangedPriceRange && hasPrice && (
                    <AccordionItem key="price-filter">
                      {({ isExpanded }) => (
                        <>
                          <AccordionButton>
                            <Box fontWeight="600" mb="2" flex="1" textAlign="left">
                              Price
                            </Box>
                            {isExpanded ? <IoChevronDownSharp /> : <IoChevronForwardSharp />}
                          </AccordionButton>
                          <AccordionPanel>
                            <PriceRangeFilter
                              isMobileView
                              handleUserChangedPriceRange={handleUserChangedPriceRange}
                            />
                          </AccordionPanel>
                        </>
                      )}
                    </AccordionItem>
                  )}
                  {attributesPreview ? (
                    <ListingFilterItems
                      filteredAttributes={attributesPreview.filter(
                        (a) => !hiddenFilterAttributeFormats.includes(a.format),
                      )}
                    />
                  ) : (
                    <ListingFilterItems
                      filteredAttributes={totalAttributes.filter(
                        (a) => a.filter_display && !hiddenFilterAttributeFormats.includes(a.format),
                      )}
                    />
                  )}
                </Accordion>
                <Flex direction="column" gap="16px" px="16px">
                  <Button colorScheme="primary" onClick={handleApply}>
                    SAVE FILTERS
                  </Button>
                  <Button variant="outline" colorScheme="primary" onClick={handleOnClose}>
                    CANCEL
                  </Button>
                </Flex>
              </Flex>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Flex>
    </Flex>
  );
};

export default ListingAttributes;
